import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { loadCheckoutWebComponents } from '@checkout.com/checkout-web-components'

import { BaseView, BaseText } from '@/components/base'
import { useTransactionStore } from '@/store/useTransactionStore'

import config from '@/config/env'
import { LoadingSpinner } from '@/components'

interface CheckoutFlowProps {
  paymentSessionToken: string
  paymentSessionSecret: string
  orderId: string
  paymentSession: any
  transactionId: string
  onSuccess: () => void
  onError: (error: string) => void
}

export const CheckoutFlow: React.FC<CheckoutFlowProps> = ({
  paymentSessionToken,
  paymentSessionSecret,
  orderId,
  paymentSession,
  transactionId,
  onSuccess,
  onError,
}) => {
  const { t } = useTranslation()
  const flowContainerRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isInitializing, setIsInitializing] = useState(true)
  const [initError, setInitError] = useState<string | null>(null)
  const checkoutInstanceRef = useRef<any>(null)
  const [isRedirecting, setIsRedirecting] = useState(false) // ✅ Redirect flag
  const clearLastTransactionData = useTransactionStore(
    (state) => state.clearLastTransactionData,
  )

  // Use refs for stable callbacks
  const onSuccessRef = useRef(onSuccess)
  const onErrorRef = useRef(onError)

  useEffect(() => {
    onSuccessRef.current = onSuccess
    onErrorRef.current = onError
  }, [onSuccess, onError])

  console.log('!!!paymentSession', paymentSession)
  console.log('!!!transactionId', transactionId)

  // ✅ Check for 3DS redirect on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const paymentId = urlParams.get('cko-payment-id')
    const success = urlParams.get('success')
    const status = urlParams.get('status')

    console.log('[CheckoutFlow] 🔍 Checking for 3DS redirect...')
    console.log('[CheckoutFlow] Current URL:', window.location.href)
    console.log('[CheckoutFlow] URL params:', {
      paymentId,
      success,
      status,
      allParams: Object.fromEntries(urlParams.entries()),
    })

    if (paymentId) {
      console.log('🎯 [CheckoutFlow] ✅ 3DS REDIRECT DETECTED!')
      console.log('[CheckoutFlow] 3DS redirect details:', {
        paymentId,
        success,
        status,
        fullUrl: window.location.href,
      })

      // Successful 3DS redirect
      if (success === 'true' || status === 'Approved') {
        console.log('✅ [CheckoutFlow] 3DS SUCCESS - Payment approved!')
        // Show toast similar to RN
        toast.success(t('messages:success.redirectingIn', { seconds: 5 }))

        // Invalidate queries like in RN and wait for refetch
        setTimeout(async () => {
          console.log(
            '[CheckoutFlow] 3DS success - Invalidating and refetching queries...',
          )

          // ✅ Invalidate and wait for refetch to complete
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: ['transactions'],
              refetchType: 'all', // Refetch all instances including inactive
            }),
            queryClient.invalidateQueries({
              queryKey: ['wallet'],
              refetchType: 'all',
            }),
            queryClient.invalidateQueries({
              queryKey: ['payment-requests/history'],
              refetchType: 'all',
            }),
          ])

          console.log(
            '[CheckoutFlow] 3DS success - Queries refreshed successfully',
          )

          // Clear transaction data
          clearLastTransactionData()

          // Navigate to activity with fresh data (same as RN)
          setTimeout(() => {
            navigate('/activity', { replace: true })
          }, 300)
        }, 4700)
      } else {
        // Failed 3DS redirect
        console.log('❌ [CheckoutFlow] 3DS FAILED - Payment rejected')
        toast.error(t('common:paymentFailed', 'Payment failed'))
        navigate('/activity', { replace: true })
      }

      // Clear URL params
      window.history.replaceState({}, '', window.location.pathname)
      return
    } else {
      console.log('[CheckoutFlow] ❌ No 3DS redirect - Normal payment flow')
    }
  }, [clearLastTransactionData, navigate, queryClient, t])

  useEffect(() => {
    const initCheckoutFlow = async () => {
      try {
        setIsInitializing(true)
        setInitError(null)

        console.log('[CheckoutFlow] Starting initialization...')
        console.log(
          '[CheckoutFlow] Payment session token:',
          paymentSessionToken,
        )

        // Step 1: Parse payment_session_token
        // Checkout.com SDK expects the token string, not the decoded object
        // Token format from backend: "YmFzZTY0:encoded_json_data"

        // Remove "YmFzZTY0:" prefix if present (it's just a marker)
        const tokenWithoutPrefix = paymentSessionToken.includes(':')
          ? paymentSessionToken.split(':')[1]
          : paymentSessionToken

        console.log(
          '[CheckoutFlow] Payment session token (base64):',
          tokenWithoutPrefix,
        )

        // Decode to verify data (for logging only)
        let decodedSessionData: any
        try {
          const decodedData = atob(tokenWithoutPrefix)
          decodedSessionData = JSON.parse(decodedData)
          console.log(
            '[CheckoutFlow] Decoded payment session (for verification):',
            {
              id: decodedSessionData.id,
              amount: decodedSessionData.amount,
              currency: decodedSessionData.currency,
              locale: decodedSessionData.locale,
            },
          )
        } catch (error) {
          console.error(
            '[CheckoutFlow] Failed to decode token for verification:',
            error,
          )
          throw new Error(
            'Invalid payment session token format. Please contact support.',
          )
        }

        // SDK expects a PaymentSessionResponse object with specific format
        // Reconstruct the format that Checkout.com SDK expects
        const paymentSession = {
          id: decodedSessionData.id,
          payment_session_secret: paymentSessionSecret,
          payment_session_token: paymentSessionToken,
          ...decodedSessionData, // Include all decoded fields
        }

        console.log('[CheckoutFlow] Reconstructed payment session:', {
          hasId: !!paymentSession.id,
          hasSecret: !!paymentSession.payment_session_secret,
          hasToken: !!paymentSession.payment_session_token,
        })

        // Step 2: Get public key and base URL from environment
        const publicKey = config.VITE_CHECKOUT_PUBLIC_KEY
        const customBaseUrl = import.meta.env.VITE_CHECKOUT_BASE_URL

        if (!publicKey) {
          throw new Error(
            'Public key not configured. Please add VITE_CHECKOUT_PUBLIC_KEY to your .env.local file.',
          )
        }

        console.log(
          '[CheckoutFlow] Public key:',
          publicKey ? 'Found' : 'Missing',
        )
        console.log(
          '[CheckoutFlow] Custom base URL:',
          customBaseUrl || 'Not configured (using default)',
        )

        // Step 3: Determine environment
        const environment = publicKey.includes('_sbox_')
          ? 'sandbox'
          : 'production'
        console.log('[CheckoutFlow] Environment:', environment)

        // Step 4: Initialize Checkout Web Components
        // According to Checkout.com docs: https://www.checkout.com/docs/payments/accept-payments/accept-a-payment-on-your-website/get-started-with-flow
        console.log('[CheckoutFlow] Initializing CheckoutWebComponents...')
        console.log('[CheckoutFlow] Configuration:', {
          hasPaymentSession: !!paymentSession,
          paymentSessionType: typeof paymentSession,
          paymentSessionLength: paymentSession?.length,
          hasPublicKey: !!publicKey,
          environment,
          locale: decodedSessionData.locale || 'en-GB',
        })
        console.log(
          '[CheckoutFlow] Payment session object keys:',
          Object.keys(paymentSession),
        )

        // Create correct payment session configuration
        // Use orderId from API response (not from decoded token!)
        console.log('[CheckoutFlow] Debug values:', {
          orderId,
          paymentSessionSecret,
          paymentSessionToken: paymentSessionToken?.substring(0, 20) + '...',
          paymentSessionKeys: paymentSession
            ? Object.keys(paymentSession)
            : 'null',
          paymentSessionOrderId: paymentSession?.order_id,
          decodedSessionId: decodedSessionData?.id,
        })

        // Try to get the correct order_id from different sources
        const correctOrderId =
          orderId || paymentSession?.order_id || decodedSessionData?.id || null

        console.log('[CheckoutFlow] Order ID resolution:', {
          orderId,
          'paymentSession?.order_id': paymentSession?.order_id,
          'decodedSessionData?.id': decodedSessionData?.id,
          correctOrderId,
        })

        // Create full PaymentSession Response object as expected by Checkout.com SDK
        const paymentSessionConfig = {
          id: correctOrderId,
          payment_session_secret: paymentSessionSecret, // Override with correct secret from API
          payment_session_token: paymentSessionToken,
          // _links: {
          //   self: {
          //     href: `https://s46vm6gm.api.sandbox.checkout.com/payment-sessions/${correctOrderId}`,
          //   },
          // },
        }

        console.log('!!!paymentSessionConfig', paymentSessionConfig)

        // Validate required fields
        if (!paymentSessionConfig.id) {
          throw new Error('Payment session ID (order_id) is missing')
        }
        if (!paymentSessionConfig.payment_session_secret) {
          throw new Error('Payment session secret is missing')
        }

        // Initialize Checkout Web Components with correct format
        console.log('[CheckoutFlow] Initializing Checkout Web Components...')

        const obj = {
          paymentSession: paymentSessionConfig,
          publicKey,
          environment: environment as 'sandbox' | 'production',
          locale: decodedSessionData.locale || 'en-GB',
        }

        console.log('!!!OBJ', obj)

        const checkout = await loadCheckoutWebComponents(obj)

        console.log(
          '[CheckoutFlow] CheckoutWebComponents initialized successfully',
        )
        checkoutInstanceRef.current = checkout

        // Step 5: Create and mount Flow component
        console.log('[CheckoutFlow] Creating Flow component...')
        const flowComponent = checkout.create('flow', {
          // ✅ Handle synchronous payment completion (same as RN)
          onPaymentCompleted: async (_component: any, paymentResponse: any) => {
            console.log('💳 [CheckoutFlow] ===============================')
            console.log('💳 [CheckoutFlow] PAYMENT COMPLETED (SYNCHRONOUS)')
            console.log('💳 [CheckoutFlow] ===============================')
            console.log(
              '[CheckoutFlow] Payment completed (synchronous):',
              paymentResponse,
            )
            console.log('[CheckoutFlow] Payment response details:', {
              id: paymentResponse?.id,
              status: paymentResponse?.status,
              approved: paymentResponse?.approved,
              response_code: paymentResponse?.response_code,
              response_summary: paymentResponse?.response_summary,
              requires3DS: paymentResponse?.['3ds']?.enrolled || false,
              fullResponse: paymentResponse,
            })

            // Check if 3DS is required
            if (
              paymentResponse?.['3ds']?.enrolled === 'Y' ||
              paymentResponse?._links?.redirect
            ) {
              console.log(
                '🔒 [CheckoutFlow] 3DS REQUIRED - User will be redirected',
              )
              console.log(
                '[CheckoutFlow] 3DS redirect URL:',
                paymentResponse?._links?.redirect?.href,
              )
              // Don't handle success here - wait for 3DS redirect
              return
            }

            console.log('✅ [CheckoutFlow] NO 3DS - Processing direct success')

            // ✅ Show loading overlay immediately
            setIsRedirecting(true)

            // Call parent success callback
            onSuccessRef.current()

            // Show success toast (same as RN)
            toast.success(t('messages:success.redirectingIn', { seconds: 5 }))

            // Wait 4 seconds for backend to process (same as RN)
            setTimeout(async () => {
              console.log(
                '[CheckoutFlow] Invalidating and refetching queries...',
              )

              // ✅ Invalidate and wait for refetch to complete
              await Promise.all([
                queryClient.invalidateQueries({
                  queryKey: ['transactions'],
                  refetchType: 'all', // Refetch all instances including inactive
                }),
                queryClient.invalidateQueries({
                  queryKey: ['wallet'],
                  refetchType: 'all',
                }),
                queryClient.invalidateQueries({
                  queryKey: ['payment-requests/history'],
                  refetchType: 'all',
                }),
              ])

              console.log('[CheckoutFlow] Queries refreshed successfully')

              // Clear transaction data
              clearLastTransactionData()

              // Navigate to activity with fresh data
              setTimeout(() => {
                navigate('/activity', { replace: true })
              }, 300)
            }, 4700)
          },

          // ✅ Handle errors (similar to RN)
          onError: (_component: any, error: any) => {
            console.log('❌ [CheckoutFlow] ===============================')
            console.log('❌ [CheckoutFlow] PAYMENT ERROR')
            console.log('❌ [CheckoutFlow] ===============================')
            console.error('[CheckoutFlow] Payment error:', error)
            console.log('[CheckoutFlow] Error details:', {
              code: error?.code,
              message: error?.message,
              details: error?.details,
              abortReason: error?.details?.abortReason,
              fullError: error,
            })

            // Log to error tracking (in RN they use Sentry)
            // Sentry.captureException(error, { tags: { type: 'payment-error' } })

            // Check if user cancelled
            if (
              error?.code === 'payment_cancelled' ||
              error?.details?.abortReason === 'card_not_supported' ||
              error?.message?.toLowerCase().includes('cancel')
            ) {
              console.log('[CheckoutFlow] Payment cancelled by user')
              toast.error(t('common:paymentCancelled', 'Payment cancelled'))
              navigate('/activity', { replace: true })
              return
            }

            // Show error message
            const errorMessage =
              error?.message ||
              t('common:paymentFailed', 'Payment failed. Please try again.')

            toast.error(errorMessage)
            onErrorRef.current(errorMessage)
          },
        })

        // Set up ready handler
        console.log('[CheckoutFlow] Setting up ready handler...')

        // Use addEventListener instead of .on() method
        const handleReady = () => {
          console.log('[CheckoutFlow] Flow component is ready')
          setIsInitializing(false)
        }

        // Try different event binding approaches for ready event
        try {
          if (typeof (flowComponent as any).on === 'function') {
            ;(flowComponent as any).on('ready', handleReady)
          } else if (
            typeof (flowComponent as any).addEventListener === 'function'
          ) {
            ;(flowComponent as any).addEventListener('ready', handleReady)
          } else {
            console.warn(
              '[CheckoutFlow] No event binding method found, setting ready state manually',
            )
            setIsInitializing(false)
          }
        } catch (eventError) {
          console.warn(
            '[CheckoutFlow] Error setting up ready event:',
            eventError,
          )
          setIsInitializing(false)
        }

        if (flowContainerRef.current) {
          // Mount to DOM element
          // According to docs: flowComponent.mount('#flow-container')
          console.log('[CheckoutFlow] Mounting Flow component...')
          flowComponent.mount('#checkout-flow-container')
          console.log('[CheckoutFlow] Flow component mounted successfully')
        } else {
          throw new Error('Flow container element not found')
        }
      } catch (error: any) {
        console.error('[CheckoutFlow] Initialization error:', error)
        const errorMessage =
          error?.message ||
          t(
            'common:failedToInitializePayment',
            'Failed to initialize payment. Please try again.',
          )
        setInitError(errorMessage)
        onErrorRef.current(errorMessage)
        setIsInitializing(false)
      }
    }

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      initCheckoutFlow()
    }, 300)

    return () => {
      clearTimeout(timer)
      // Cleanup checkout instance if it exists
      if (checkoutInstanceRef.current?.destroy) {
        try {
          checkoutInstanceRef.current.destroy()
          console.log('[CheckoutFlow] Checkout instance destroyed')
        } catch (err) {
          console.warn('[CheckoutFlow] Error destroying checkout:', err)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    paymentSessionToken,
    paymentSessionSecret,
    orderId,
    transactionId,
    // onSuccess and onError via refs, not needed in dependencies
    // navigate and t can also cause re-renders, but are used inside callbacks
  ])

  if (initError) {
    return (
      <BaseView flex={1} p={16} justify="center" align="center">
        <BaseView mb={12}>
          <BaseText
            fontSize={16}
            fontFamily="medium"
            color="label.error"
            textAlign="center"
          >
            {t('common:errorOccurred', 'An error occurred')}
          </BaseText>
        </BaseView>
        <BaseView mb={24}>
          <BaseText fontSize={14} color="label.secondary" textAlign="center">
            {initError}
          </BaseText>
        </BaseView>
        <BaseText fontSize={12} color="label.tertiary" textAlign="center">
          {t(
            'common:pleaseContactSupport',
            'Please try again or contact support',
          )}
        </BaseText>
      </BaseView>
    )
  }

  return (
    <BaseView flex={1} style={{ position: 'relative' }}>
      {/* Loading overlay during initialization */}
      {isInitializing && (
        <BaseView
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#131415',
          }}
        >
          {/* <BaseView
            style={{
              width: '50px',
              height: '50px',
              border: '3px solid rgba(255, 255, 255, 0.1)',
              borderTop: '3px solid #00CF46',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
          <BaseView mt={16}>
            <BaseText fontSize={14} color="label.default" textAlign="center">
              {t('common:initializingPayment', 'Initializing payment form...')}
            </BaseText>
          </BaseView> */}
          <LoadingSpinner />
        </BaseView>
      )}

      {/* ✅ Success/Redirecting overlay */}
      {isRedirecting && (
        <BaseView
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 20,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(19, 20, 21, 0.95)', // Darker background
          }}
          className="ym-hide-content"
        >
          {/* Success checkmark animation */}
          <BaseView
            style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: '#00CF46',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: '24px',
              animation: 'scaleIn 0.3s ease-out',
            }}
          >
            <BaseText fontSize={48} style={{ color: 'white', lineHeight: '1' }}>
              ✓
            </BaseText>
          </BaseView>

          <BaseView mb={16}>
            <BaseText
              fontSize={20}
              fontFamily="semibold"
              color="label.default"
              textAlign="center"
            >
              {t('common:paymentSuccessful', 'Payment Successful!')}
            </BaseText>
          </BaseView>

          <BaseView
            style={{
              width: '40px',
              height: '40px',
              border: '3px solid rgba(255, 255, 255, 0.1)',
              borderTop: '3px solid #00CF46',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />

          <BaseView mt={16}>
            <BaseText fontSize={14} color="label.secondary" textAlign="center">
              {t(
                'common:redirectingToActivity',
                'Redirecting to your transactions...',
              )}
            </BaseText>
          </BaseView>
        </BaseView>
      )}

      {/* Checkout Flow Container */}
      <div
        id="checkout-flow-container"
        ref={flowContainerRef}
        style={{
          width: '100%',
          minHeight: '500px',
          padding: '16px',
          // ✅ Allow natural content flow and scrolling
          position: 'relative',
          overflow: 'visible',
        }}
      />

      {/* CSS for animations */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes scaleIn {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        /* Checkout Flow custom styles */
        #checkout-flow-container {
          font-family: inherit;
        }

        /* Override Checkout.com styles to match app theme */
        #checkout-flow-container input,
        #checkout-flow-container button {
          font-family: inherit;
        }
      `}</style>
    </BaseView>
  )
}
